import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart3,
  Brain,
  Cog,
  LineChart,
  Target,
  PiggyBank,
  TrendingUp,
  Store,
  CheckCircle,
  ArrowRight,
  Search,
  Settings,
  BarChart2,
  Lightbulb,
  ShoppingCart,
  Briefcase,
  Megaphone,
  ChevronRight,
  MapPin,
  Clock,
  Mail,
  Zap,
  Shield,
  Eye,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20me%20interesa%20el%20servicio%20de%20analisis%20de%20datos%20para%20mi%20empresa";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const FAQS = [
  {
    q: "¿Para qué tipo de empresas funciona el análisis de datos para empresas?",
    a: "Funciona en cualquier negocio que facture, compre, venda o tenga clientes. PYMEs, industria, distribución, agroalimentaria o servicios profesionales: si hay datos, hay margen para mejorar resultados con análisis de datos para empresas."
  },
  {
    q: "¿Qué información concreta voy a obtener con un análisis de datos para mi empresa?",
    a: "Información accionable: qué clientes son rentables y cuáles no, qué productos generan margen real, dónde se está fugando dinero y qué procesos son ineficientes. No informes para archivar, sino respuestas para decidir."
  },
  {
    q: "¿Necesito un equipo técnico interno para contratar un servicio de análisis de datos?",
    a: "No. Yo aporto el conocimiento técnico; tu empresa solo necesita facilitar el acceso a los datos existentes. La idea es precisamente que no tengas que contratar a nadie a tiempo completo para tener este nivel de análisis."
  },
  {
    q: "¿En qué se diferencia el análisis de datos para empresas de los informes de mi gestoría?",
    a: "Tu gestoría te dice cuánto facturaste y cuánto pagaste de impuestos. El análisis de datos para empresas te explica por qué pasa lo que pasa: qué cliente abandona, qué línea pierde dinero o qué decisión está descontrolando los costes."
  },
  {
    q: "¿Con qué sectores trabajáis habitualmente?",
    a: "Trabajo con empresas de industria, distribución, agroalimentaria, logística, servicios profesionales y comercio en Murcia y resto de España. La metodología es la misma; lo que cambia son las preguntas concretas que respondemos con los datos."
  },
  {
    q: "¿Cuándo empezaré a ver retorno del análisis de datos en mi cuenta de resultados?",
    a: "Los primeros resultados accionables suelen llegar en 2-3 semanas. El retorno económico depende de cuánto tarde tu empresa en aplicar las recomendaciones, pero la mayoría de clientes recuperan la inversión en menos de 6 meses."
  }
];

export default function AnalisisDatosEmpresas() {
  useEffect(() => {
    document.title = "Análisis de Datos para Empresas | Mejora resultados | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Servicios de análisis de datos para empresas. Descubre patrones, mejora decisiones y aumenta la rentabilidad de tu negocio.");
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/analisis-datos-empresas");
    }

    // FAQPage JSON-LD para rich snippets en Google
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "analisis-faq-jsonld";
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    });
    document.head.appendChild(ldScript);

    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc) metaDesc.setAttribute("content", "Transforma los datos de tu empresa en decisiones rentables. Servicios de análisis de datos, business intelligence y modelos predictivos en Murcia.");
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
      const existing = document.getElementById("analisis-faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden">
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="page-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4" data-testid="nav-logo-link">
              <img src={LOGO_URL} alt="DATYON - Análisis de Datos para Empresas" className="h-20 w-auto" style={{ maxHeight: '80px' }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/proceso" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Proceso</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero - H1 orientado a la keyword */}
        <section className="relative min-h-[80vh] flex items-center section-padding pt-24 overflow-hidden" data-testid="seo-hero">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="spotlight-2 top-1/3 right-0" />
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
              <motion.p variants={fadeInUp} className="text-indigo-400 font-medium mb-4 text-sm md:text-base">
                DATYON — Consultoría de datos profesional
              </motion.p>
              <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                Análisis de datos para{" "}
                <span className="text-gradient">empresas</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg mb-8 max-w-2xl">
                Transformamos la información de tu empresa en decisiones claras y rentables. 
                Detectamos patrones ocultos, optimizamos procesos y mejoramos tu rentabilidad 
                mediante análisis de datos profesional, Business Intelligence y modelos predictivos.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="seo-hero-cta">
                  <Link to="/contacto">
                    Solicita tu auditoría gratuita
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30" data-testid="seo-hero-whatsapp">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Contactar por WhatsApp
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Qué es el análisis de datos para empresas */}
        <section className="relative section-padding bg-slate-900/50" data-testid="seo-what-is">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                  Qué es el <span className="text-gradient">análisis de datos</span> para empresas
                </h2>
                <p className="text-slate-400 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
                  El análisis de datos para empresas consiste en examinar la información que tu negocio 
                  genera cada día para descubrir patrones, detectar problemas ocultos y encontrar 
                  oportunidades de crecimiento. En lugar de tomar decisiones basadas en intuición, 
                  te permite actuar con evidencia real.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: <BarChart3 className="w-7 h-7" />, title: "Análisis de Datos", desc: "Analizamos la información de tu empresa para descubrir patrones, oportunidades de crecimiento y problemas ocultos." },
                  { icon: <LineChart className="w-7 h-7" />, title: "Business Intelligence", desc: "Diseñamos dashboards interactivos para que tengas control total de tu negocio en tiempo real." },
                  { icon: <Brain className="w-7 h-7" />, title: "Modelos Predictivos", desc: "Creamos modelos matemáticos capaces de anticipar comportamientos futuros como ventas o abandono de clientes." },
                  { icon: <Cog className="w-7 h-7" />, title: "Automatización con IA", desc: "Automatizamos tareas repetitivas como informes, clasificación de datos y procesamiento de documentos." }
                ].map((service, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`seo-service-${i}`}>
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4">
                          {service.icon}
                        </div>
                        <h3 className="font-semibold text-white mb-2 text-lg">{service.title}</h3>
                        <p className="text-slate-400 text-sm">{service.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeInUp} className="text-center mt-8">
                <Link to="/servicios" className="text-indigo-400 hover:text-indigo-300 font-medium inline-flex items-center gap-2 transition-colors">
                  Ver todos nuestros servicios en detalle
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Por qué tu empresa necesita análisis de datos */}
        <section className="relative section-padding overflow-hidden" data-testid="seo-why">
          <div className="spotlight-1 top-0 right-0" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Por qué tu empresa necesita análisis de datos
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Muchas empresas pierden oportunidades cada día porque no aprovechan la información que ya tienen.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-8">
                <motion.div variants={fadeInUp}>
                  <div className="glass-card rounded-2xl p-8">
                    <h3 className="text-red-400 font-semibold mb-6 flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Sin análisis de datos
                    </h3>
                    <ul className="space-y-4">
                      {[
                        "No saben qué productos son realmente rentables",
                        "Invierten en marketing sin medir el retorno",
                        "Tienen stock mal optimizado",
                        "Toman decisiones basadas en intuición"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-300">
                          <Target className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <div className="glass-card rounded-2xl p-8">
                    <h3 className="text-cyan-400 font-semibold mb-6 flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Con análisis de datos
                    </h3>
                    <ul className="space-y-4">
                      {[
                        "Conoces la rentabilidad real de cada producto y cliente",
                        "Mides el retorno de cada campaña de marketing",
                        "Optimizas inventario con predicciones de demanda",
                        "Tomas decisiones basadas en evidencia real"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-300">
                          <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Cómo trabajamos */}
        <section className="relative section-padding bg-slate-900/50" data-testid="seo-process">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Cómo implementamos el análisis de datos en tu empresa
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Un proceso estructurado en cuatro fases para convertir tus datos en decisiones claras.
                </p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { num: "01", icon: <Search className="w-6 h-6" />, title: "Diagnóstico inicial gratuito", desc: "Analizamos qué datos tiene tu empresa, qué herramientas usas y qué decisiones necesitas mejorar." },
                  { num: "02", icon: <BarChart2 className="w-6 h-6" />, title: "Análisis y detección", desc: "Detectamos patrones, oportunidades de crecimiento y problemas ocultos en tus datos." },
                  { num: "03", icon: <Settings className="w-6 h-6" />, title: "Implementación", desc: "Creamos dashboards, modelos predictivos o automatizaciones adaptadas a tu negocio." },
                  { num: "04", icon: <Lightbulb className="w-6 h-6" />, title: "Decisiones con datos", desc: "Puedes tomar decisiones claras basadas en información real, no en suposiciones." }
                ].map((step, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover relative overflow-hidden">
                      <CardContent className="p-6">
                        <span className="absolute top-4 right-4 text-5xl font-bold text-slate-800/50">{step.num}</span>
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4">
                          {step.icon}
                        </div>
                        <h3 className="font-semibold text-white mb-2">{step.title}</h3>
                        <p className="text-slate-400 text-sm">{step.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Casos de uso por sector */}
        <section className="relative section-padding overflow-hidden" data-testid="seo-use-cases">
          <div className="spotlight-2 bottom-0 left-0" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Análisis de datos adaptado a tu <span className="text-gradient">sector</span>
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Cada tipo de empresa tiene necesidades distintas. Adaptamos el análisis de datos a los problemas concretos de tu sector.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    icon: <Briefcase className="w-6 h-6" />,
                    title: "Empresas de servicios",
                    items: ["Medir rentabilidad por proyecto", "Control de productividad", "Previsión de ingresos", "Análisis de clientes más rentables"]
                  },
                  {
                    icon: <Store className="w-6 h-6" />,
                    title: "Retail y tiendas físicas",
                    items: ["Predicción de ventas por temporada", "Optimización de inventario", "Análisis de ticket medio", "Horarios óptimos de personal"]
                  },
                  {
                    icon: <ShoppingCart className="w-6 h-6" />,
                    title: "Ecommerce",
                    items: ["Saber qué productos generan más beneficio", "Prever roturas de stock", "Analizar comportamiento de clientes", "Optimizar campañas publicitarias"]
                  },
                  {
                    icon: <Megaphone className="w-6 h-6" />,
                    title: "Marketing y agencias",
                    items: ["Medición real de campañas", "Atribución de conversiones", "Automatización de reporting", "Segmentación avanzada de clientes"]
                  }
                ].map((sector, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`seo-sector-${i}`}>
                      <CardContent className="p-6 md:p-8">
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400">
                            {sector.icon}
                          </div>
                          <h3 className="text-xl font-bold text-white">{sector.title}</h3>
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {sector.items.map((item, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                              <CheckCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Resultados reales */}
        <section className="relative section-padding bg-slate-900/50" data-testid="seo-results">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Resultados reales del análisis de datos
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Estos son ejemplos de resultados obtenidos aplicando análisis de datos en empresas reales.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    type: "Ecommerce",
                    problem: "Muchas ventas pero poco beneficio.",
                    solution: "Analizamos los márgenes por producto y detectamos que el 30% de las ventas generaba pérdidas debido a costes logísticos.",
                    result: "Se ajustó la estrategia de precios y promociones",
                    metric: "+18%", label: "beneficio"
                  },
                  {
                    type: "Servicios",
                    problem: "No sabían qué clientes eran rentables.",
                    solution: "Creamos un dashboard que medía horas reales por proyecto y coste asociado.",
                    result: "Detectaron clientes no rentables y reorganizaron su cartera",
                    metric: "+25%", label: "rentabilidad anual"
                  },
                  {
                    type: "Retail",
                    problem: "Exceso de stock y roturas frecuentes.",
                    solution: "Modelo predictivo de demanda por temporada.",
                    result: "Reducción del stock inmovilizado",
                    metric: "-32%", label: "stock inmovilizado"
                  }
                ].map((c, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full success-card border-slate-800 card-hover overflow-hidden" data-testid={`seo-case-${i}`}>
                      <CardContent className="p-6 md:p-8">
                        <span className="inline-block px-3 py-1 rounded-full bg-indigo-600/20 text-indigo-400 text-xs font-medium mb-4">
                          {c.type}
                        </span>
                        <div className="space-y-4 mb-6">
                          <div>
                            <p className="text-red-400 text-xs font-medium mb-1">PROBLEMA</p>
                            <p className="text-slate-300 text-sm">{c.problem}</p>
                          </div>
                          <div>
                            <p className="text-cyan-400 text-xs font-medium mb-1">SOLUCIÓN</p>
                            <p className="text-slate-300 text-sm">{c.solution}</p>
                          </div>
                          <div>
                            <p className="text-green-400 text-xs font-medium mb-1">RESULTADO</p>
                            <p className="text-slate-300 text-sm">{c.result}</p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-slate-700 text-center">
                          <p className="text-3xl md:text-4xl font-bold text-gradient">{c.metric}</p>
                          <p className="text-slate-400 text-sm">{c.label}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Herramientas */}
        <section className="relative section-padding" data-testid="seo-tools">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Herramientas que utilizamos
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Utilizamos herramientas modernas de análisis de datos para transformar información compleja en decisiones claras.
                </p>
              </motion.div>
              <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
                {["Power BI", "Python", "SQL", "Looker Studio", "Tableau"].map((tool, i) => (
                  <div key={i} className="px-6 py-3 rounded-full bg-slate-800 border border-slate-700 text-white font-medium">
                    {tool}
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* SEO Content Block */}
        <section className="relative section-padding" data-testid="seo-content-block">
          <div className="max-w-4xl mx-auto">
            <div className="text-slate-400 text-base leading-relaxed space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">Qué es el análisis de datos para empresas y por qué lo necesitas</h2>

              <p>
                El análisis de datos para empresas consiste en revisar la información que ya genera tu negocio — ventas, costes, stock, clientes, operaciones — y convertirla en decisiones claras que mejoren los resultados. No se trata de hacer gráficos bonitos ni de acumular métricas. Se trata de encontrar respuestas a preguntas concretas: dónde pierdes dinero, qué funciona y qué no, y qué deberías cambiar para mejorar.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Para quién es este servicio</h3>

              <p>
                Este servicio está pensado para empresas que ya tienen datos pero no los están aprovechando. PYMES, empresas de distribución, industria, agroalimentaria o logística que toman decisiones basándose en intuición, en un Excel desactualizado o en la experiencia del responsable de turno. Si sientes que tu empresa podría funcionar mejor pero no sabes exactamente qué cambiar, probablemente el problema está en los datos que no estás mirando.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Qué problemas resuelve el análisis de datos</h3>

              <p>
                Muchas empresas pierden dinero sin saberlo porque no tienen visibilidad real de lo que ocurre en su negocio. El análisis de datos ayuda a detectar productos que no son rentables aunque se vendan mucho, clientes que generan mucho volumen pero poco margen, gastos operativos que podrían reducirse, campañas comerciales que no están dando retorno y procesos ineficientes que afectan a la productividad. Todas estas situaciones tienen un impacto directo en la cuenta de resultados, y los datos permiten identificarlas y cuantificarlas.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Ejemplos reales de lo que se consigue</h3>

              <p>
                He trabajado con empresas de distribución alimentaria donde descubrimos que el 30% del catálogo generaba pérdidas por costes logísticos ocultos. Con fabricantes industriales donde reorganizamos turnos de producción tras analizar el rendimiento real por línea y operario. Con empresas agroalimentarias donde un modelo de previsión de demanda redujo el stock inmovilizado en un 32% en menos de dos meses. No son casos teóricos. Son resultados medibles que empiezan con un buen análisis de los datos que la empresa ya tiene.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-4">Beneficios de analizar los datos de tu empresa</h3>

              <p>
                El principal beneficio es dejar de tomar decisiones a ciegas. Cuando analizas tus datos correctamente, puedes saber con exactitud qué te da margen y qué no, dónde recortar sin perder calidad, cómo asignar mejor tus recursos y qué oportunidades estás dejando pasar. Eso se traduce en más beneficio, menos costes y una gestión mucho más eficiente. Si quieres ver cómo trabajo en detalle, puedes consultar la página de <Link to="/consultor-datos-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">consultor de datos en Murcia</Link>, ver el rol más operativo de <Link to="/analista-datos-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">analista de datos en Murcia</Link> o leer el artículo sobre <Link to="/blog/como-saber-si-tu-empresa-pierde-dinero" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">cómo saber si tu empresa está perdiendo dinero</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative section-padding overflow-hidden" data-testid="analisis-faq">
          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="analisis-h2-faq">
                  Preguntas frecuentes sobre el <span className="text-gradient">análisis de datos para empresas</span>
                </h2>
                <p className="text-slate-400 text-base leading-relaxed">
                  Las dudas más habituales antes de poner los datos de tu empresa a trabajar.
                </p>
              </motion.div>

              <div className="space-y-4">
                {FAQS.map((faq, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="bg-slate-900 border-slate-800" data-testid={`analisis-faq-${i}`}>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-white mb-3 flex items-start gap-3">
                          <HelpCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                          {faq.q}
                        </h3>
                        <p className="text-slate-400 text-sm leading-relaxed pl-8">{faq.a}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA final */}
        <section className="relative section-padding bg-slate-900/50" data-testid="seo-final-cta">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="glass-card rounded-2xl p-8 md:p-12 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Empieza a tomar decisiones con datos reales
                </h2>
                <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
                  En el diagnóstico inicial gratuito analizamos qué datos tiene tu empresa, 
                  qué decisiones necesitas mejorar y qué oportunidades existen. 
                  Después te proponemos una solución adaptada a tu negocio.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                  <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 rounded-full" data-testid="seo-cta-contact">
                    <Link to="/contacto">
                      Solicita tu auditoría gratuita
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 rounded-full transition-all hover:scale-105" data-testid="seo-cta-whatsapp">
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Escríbenos por WhatsApp
                    </a>
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-400 text-sm">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-400" />
                    Respuesta en menos de 24 horas
                  </span>
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-indigo-400" />
                    <a href="mailto:info@datyon.com" className="hover:text-white transition-colors">info@datyon.com</a>
                  </span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800" data-testid="seo-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        data-testid="seo-whatsapp-float" aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          ¿Hablamos?
        </span>
      </a>

      <CookieBanner />
    </div>
  );
}
